import type { IntlShape } from 'react-intl';
import type { AssetKind } from 'fym-shared/src/music24/assetKinds';
import { STUDIO_THEMES } from 'fym-shared/src/music24/studioThemes';
import type {
  AssetsLibraryItem,
  AssetsLibrarySong,
} from '../../../../../../../../music24-api/src/kit/assetsLibrary/assetsLibraryTypes';
import type { UploadFile } from '../../../../../../../../music24-api/src/kit/posts/listUploads';
import { SMLRouteNames } from '../../../../../../routing/sml/SMLRoutes';
import { SerializedKitAsset } from '../../../../../../redux/api/fymAPI.types';
import { kitVariantLabel } from '../../../kitLabels';
import { studioThemeMessages } from '../../../studio/studioThemeMessages';
import { allFilesMessages } from './allFilesMessages';

export type { AssetsLibraryItem, AssetsLibrarySong, UploadFile };

/**
 * The "All files" search of Releases (design assets-rethink d4, decision 8: no Library page). It
 * reads `assets.list` (the archived files too) and `assets.uploads`, and groups the rows by kind:
 * Release kit, Posts, Your uploads. A row opens the file in its home. Pure, so the rules are tested
 * without React.
 */

export type AllFilesGroupId = 'keepers' | 'posts' | 'uploads';

export interface AllFilesRow {
  key: string;
  group: AllFilesGroupId;
  label: string;
  place: string;
  /** The words that the search matches: the song title and the label. */
  haystack: string;
  /** The relative url of a small picture, or null. */
  pictureUrl: string | null;
  /** Where a click goes: a route of the app, or a file (an upload) that opens in a new tab. */
  target: { kind: 'route'; path: string } | { kind: 'file'; url: string };
}

export interface AllFilesGroup {
  id: AllFilesGroupId;
  rows: AllFilesRow[];
}

const GROUP_OF_KIND: Readonly<Record<AssetKind, AllFilesGroupId>> = { keeper: 'keepers', post: 'posts' };

/** The words of a query, lower case. */
export function searchWords(query: string): string[] {
  return query
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word !== '');
}

/**
 * The word of the query that names a song: the server search (`assets.list` `query`) takes the song
 * title only, so one word that a title holds goes to the server and the other words filter the
 * labels here. Null when no word names a song (then the newest files are read and filtered here).
 */
export function songWordOf(words: string[], titles: string[]): string | null {
  const lower = titles.map((title) => title.toLowerCase());
  return words.find((word) => lower.some((title) => title.includes(word))) ?? null;
}

export function matchesWords(haystack: string, words: string[]): boolean {
  const text = haystack.toLowerCase();
  return words.every((word) => text.includes(word));
}

function kitLabel(intl: IntlShape, asset: SerializedKitAsset): string {
  const name = intl.formatMessage(studioThemeMessages[asset.themeId].name);
  const namesVariant =
    asset.themeId === 'story' || asset.themeId === 'feedPost' || STUDIO_THEMES[asset.themeId].outputs.length > 1;
  return namesVariant ? `${name} (${kitVariantLabel(intl, { variant: asset.variant, specId: asset.specId })})` : name;
}

/** "Story (Out now)", "Lyric video", "AI reel 30s", "Cover". */
export function itemLabel(intl: IntlShape, item: AssetsLibraryItem): string {
  if (item.kind === 'reel') {
    return intl.formatMessage(allFilesMessages.reel, { length: item.reel.variant });
  }
  if (item.kind === 'cover') {
    return intl.formatMessage(allFilesMessages.cover);
  }
  return kitLabel(intl, item.asset as unknown as SerializedKitAsset);
}

function uuidOf(item: AssetsLibraryItem): string {
  if (item.kind === 'kitAsset') {
    return item.asset.uuid;
  }
  return item.kind === 'reel' ? item.reel.uuid : item.cover.uuid;
}

/** The home of a file (design d1): the Kit tab, Posts or the archive. */
export function itemPath(item: AssetsLibraryItem): string {
  const song = item.catalogItemUuid ?? '';
  const release = `/${SMLRouteNames.promote}/${SMLRouteNames.promoteRelease}`;
  const posts = `/${SMLRouteNames.promote}/${SMLRouteNames.promotePosts}`;
  if (item.assetKind === 'keeper' && song !== '') {
    const query = new URLSearchParams({ catalogItemUuid: song, tab: 'kit' });
    if (item.kind !== 'cover' && item.state === 'ready') {
      query.set('asset', uuidOf(item));
    }
    return `${release}?${query.toString()}`;
  }
  if (item.archivedAt !== null) {
    return `${posts}?view=archive`;
  }
  const query = new URLSearchParams();
  if (song !== '') {
    query.set('catalogItemUuid', song);
  }
  if (item.state === 'ready') {
    query.set('asset', uuidOf(item));
  }
  const text = query.toString();
  return text === '' ? posts : `${posts}?${text}`;
}

/** The relative url of a small picture of a file, or null (a file that is not ready has none). */
export function itemPicture(item: AssetsLibraryItem): string | null {
  if (item.kind === 'kitAsset') {
    const asset = item.asset as unknown as SerializedKitAsset;
    return item.state === 'ready' ? (asset.thumbUrl ?? asset.posterUrl ?? asset.fileUrl) : null;
  }
  if (item.kind === 'reel') {
    return item.reel.video?.posterUrl ?? null;
  }
  return item.cover.thumbUrl ?? item.cover.url;
}

function itemPlace(intl: IntlShape, item: AssetsLibraryItem, song: string): string {
  if (item.state === 'failed') {
    return intl.formatMessage(allFilesMessages.placeFailed, { song });
  }
  if (item.state === 'queued' || item.state === 'working') {
    const percent = item.kind === 'kitAsset' ? item.asset.progress : item.kind === 'reel' ? item.reel.progress : 0;
    return intl.formatMessage(allFilesMessages.placeMaking, { song, percent });
  }
  if (item.assetKind === 'keeper') {
    return intl.formatMessage(allFilesMessages.placeKit, { song });
  }
  return item.archivedAt !== null
    ? intl.formatMessage(allFilesMessages.placeArchive, { song })
    : intl.formatMessage(allFilesMessages.placePost, { song });
}

export function itemRow(
  intl: IntlShape,
  item: AssetsLibraryItem,
  songs: Record<string, AssetsLibrarySong>
): AllFilesRow {
  const title =
    item.catalogItemUuid === null
      ? intl.formatMessage(allFilesMessages.noSong)
      : (songs[item.catalogItemUuid]?.title ?? intl.formatMessage(allFilesMessages.noSong));
  const label = itemLabel(intl, item);
  return {
    key: item.key,
    group: GROUP_OF_KIND[item.assetKind],
    label,
    place: itemPlace(intl, item, title),
    haystack: `${title} ${label}`,
    pictureUrl: itemPicture(item),
    target: { kind: 'route', path: itemPath(item) },
  };
}

export function uploadRow(intl: IntlShape, upload: UploadFile): AllFilesRow {
  const label = upload.name ?? intl.formatMessage(allFilesMessages.untitled);
  const place =
    upload.usedAs === 'post'
      ? intl.formatMessage(allFilesMessages.placeUploadPost)
      : upload.usedAs === 'logo'
        ? intl.formatMessage(allFilesMessages.placeUploadLogo)
        : intl.formatMessage(allFilesMessages.placeUploadComposer);
  return {
    key: `upload:${upload.uuid}`,
    group: 'uploads',
    label,
    place,
    haystack: label,
    pictureUrl: upload.mediaKind === 'image' ? upload.url : null,
    target: { kind: 'file', url: upload.url },
  };
}

/** The rows that match every word, in the groups of design d4 (an empty group is left out). */
export function groupAllFiles(rows: AllFilesRow[], words: string[]): AllFilesGroup[] {
  const kept = rows.filter((row) => matchesWords(row.haystack, words));
  return (['keepers', 'posts', 'uploads'] as const)
    .map((id) => ({ id, rows: kept.filter((row) => row.group === id) }))
    .filter((group) => group.rows.length > 0);
}
